/**
 * PRICING KNOWLEDGE BASE
 * Central configuration for all pricing rules and data sources
 *
 * This structure enables:
 * - Easy updates to amenity premiums without touching algorithm code
 * - Adding new data sources as they become available
 * - Clear versioning for annual updates (e.g., HUD FMR)
 * - Single source of truth for pricing logic
 */

export const pricingKnowledgeBase = {
  // Data sources registry
  sources: {
    rentcast: {
      name: 'Rentcast API',
      type: 'primary',
      description: 'Live market data from rental listings',
      updateFrequency: 'live',
      fields: ['medianRent', 'lowRange', 'highRange', 'sampleSize']
    },
    hudFmr: {
      name: 'HUD Fair Market Rent',
      type: 'floor',
      description: 'Federal baseline rent for Section 8 / Housing Choice Voucher',
      updateFrequency: 'annual',
      fields: ['floorRent'],
      fiscalYear: 2026,
      effectiveDate: '2025-10-01',
      nextUpdate: '2026-10-01'
    },
    staticPhilly: {
      name: 'Philadelphia Market Data',
      type: 'fallback',
      description: 'Curated local market data when API unavailable',
      updateFrequency: 'manual',
      fields: ['medianRent', 'lowRange', 'highRange']
    }
  },

  // Amenity adjustments (in dollars)
  amenityPremiums: {
    parking: {
      garage: 125,
      dedicated: 85,
      street: 35
    },
    hvac: {
      central: 75,
      'mini-split': 65,
      hybrid: 50,                  // Ductless mini-split + window units provided
      'split-window-allowed': 45,  // Ductless mini-split + window AC allowed
      'window-provided': 35,
      'window-optional': 10
    },
    laundry: {
      'in-unit': 100,
      'in-building': 25
    },
    other: {
      outdoor: { base: 75, largePremium: 100 }, // largePremium for 2+ BR
      renovated: 150,
      petFriendly: 50,
      dishwasher: 25
    }
  },

  // Condition multipliers (applied to base rent)
  conditionMultipliers: {
    excellent: 1.10,  // +10%
    good: 1.00,       // baseline
    fair: 0.95,       // -5%
    poor: 0.85        // -15%
  },

  // Square footage adjustments
  sqftAdjustment: {
    perSqftDifference: 0.15,  // $0.15 per sqft above/below median
    minimumImpact: 25         // Only apply if adjustment > $25
  },

  // Bathroom adjustments
  bathroomAdjustment: {
    extraBathPremium: 50,     // $50 per extra bathroom
    missingBathPenalty: 75    // $75 penalty per missing half-bath
  },

  // Expected bathrooms by bedroom count
  expectedBathrooms: {
    0: 1,    // Studio
    1: 1,
    2: 1,
    3: 2,
    4: 2.5
  },

  // Median sqft by bedroom count
  medianSqftByBedroom: {
    0: 550,   // Studio
    1: 750,
    2: 950,
    3: 1200,
    4: 1500
  },

  // Pricing rounding
  roundToNearest: 25,  // Round prices to nearest $25

  // Utility allowances by bedroom count (Philadelphia-specific)
  // HUD FMR includes utilities; subtract these when tenant pays
  // Source: PHA utility allowance schedules
  utilityAllowances: {
    0: 90,   // Studio
    1: 110,  // 1 BR
    2: 140,  // 2 BR
    3: 170,  // 3 BR
    4: 200   // 4 BR
  },

  // Utility payment options with multipliers
  // Multiplier applied to full utility allowance
  utilityOptions: {
    'included': { label: 'All utilities included', multiplier: 0 },
    'tenant-all': { label: 'Tenant pays all utilities', multiplier: 1.0 },
    'tenant-electric-gas': { label: 'Tenant pays electric & gas', multiplier: 0.75 },
    'tenant-electric': { label: 'Tenant pays electric only', multiplier: 0.45 }
  }
};

// Helper function to get amenity premium
export function getAmenityPremium(category, type, bedrooms = 2) {
  const premiums = pricingKnowledgeBase.amenityPremiums;

  if (category === 'other' && type === 'outdoor') {
    return bedrooms >= 2
      ? premiums.other.outdoor.largePremium
      : premiums.other.outdoor.base;
  }

  return premiums[category]?.[type] || 0;
}

// Helper function to get condition multiplier
export function getConditionMultiplier(condition) {
  return pricingKnowledgeBase.conditionMultipliers[condition] || 1.00;
}

// Helper function to calculate parking premiums (multi-select)
export function calculateParkingPremium(parkingOptions) {
  const premiums = pricingKnowledgeBase.amenityPremiums.parking;
  let total = 0;

  if (parkingOptions.garage) total += premiums.garage;
  if (parkingOptions.dedicated) total += premiums.dedicated;
  if (parkingOptions.street) total += premiums.street;

  return total;
}

// Helper function to calculate utility adjustment
// Returns a negative number (deduction) when tenant pays utilities
export function calculateUtilityAdjustment(utilityOption, bedrooms) {
  const allowances = pricingKnowledgeBase.utilityAllowances;
  const options = pricingKnowledgeBase.utilityOptions;

  const baseAllowance = allowances[bedrooms] || allowances[2];
  const multiplier = options[utilityOption]?.multiplier || 0;

  // Return negative value (deduction from HUD base)
  return -(baseAllowance * multiplier);
}

// Get utility option label for display
export function getUtilityLabel(utilityOption) {
  return pricingKnowledgeBase.utilityOptions[utilityOption]?.label || 'Unknown';
}

export default pricingKnowledgeBase;
